import { useMemo } from "react";
import { occupancySparklinePoints, type OccupancyRangeGroup } from "@/lib/analytics/occupancy";
import type { SparkPoint } from "@/components/cockpit/metrics/MetricSparkline";
import type { OrgCapacityRangeResponse } from "@/types/capacity";

/** The days the Dashboard's occupancy sparkline covers, the selected day included (phone: 30). */
export const OCCUPANCY_SPARK_DAYS = 30;

/**
 * The 30-day occupancy sparkline of the Dashboard's Occupancy card (#2175 C1,
 * the phone's hero sparkline): `capacity/overview/range` over the last
 * `OCCUPANCY_SPARK_DAYS` days in ISO weeks, the groups in scope weighted by
 * today's weighted places (`occupancySparkline`). Each point carries the start
 * date of its week. The range comes from the Dashboard's sources.
 */
export function useOccupancySparkline(
  range: { data?: OrgCapacityRangeResponse },
  inScope: (groupId: string) => boolean,
  weightedPlaces: ReadonlyMap<string, number>,
) {
  const points = useMemo<SparkPoint[]>(() => {
    const groups: (OccupancyRangeGroup & { starts: (string | undefined)[] })[] = (range.data?.groups ?? []).map((g) => ({
      group_id: g.group_id ?? "",
      weeks: (g.weeks ?? []).map((w) => ({
        summary: {
          average_weighted_utilization: w.summary?.average_weighted_utilization ?? 0,
          average_headcount_utilization: w.summary?.average_headcount_utilization ?? 0,
        },
      })),
      starts: (g.weeks ?? []).map((w) => w.week_start),
    }));
    const starts = groups.filter((g) => inScope(g.group_id)).reduce<(string | undefined)[]>(
      (longest, g) => (g.starts.length > longest.length ? g.starts : longest),
      [],
    );
    return occupancySparklinePoints(groups, inScope, weightedPlaces).map((p) => ({
      period: starts[p.week] ?? "",
      value: p.value,
    }));
  }, [range.data, inScope, weightedPlaces]);
  return { points };
}
